import * as prismic from "@prismicio/client";
import { createClient } from "@/lib/prismic";
import { getReadingTimeFromBlocks } from "@/lib/reading-time";
import { formatDate } from "@/lib/format-date";
import type { DynamicCardItem } from "./dynamic-cards";

/** The archive's own taxonomy, keyed by document UID.
 *
 *  This is the one card field Prismic cannot supply today: `proxy_article` has no
 *  category field, and every document carries the same `"Proxy Server"` tag (three
 *  carry none at all), so tags can't stand in for it either. Moving it into the CMS
 *  means either re-tagging all 16 documents with these values or adding a Select
 *  field to the custom type — both are editorial changes, not code ones.
 *
 *  A UID missing from this map still renders; it just falls back to a generic
 *  category and the default accent, so publishing a new article in Prismic never
 *  breaks the grid. */
const CATEGORY_BY_UID: Record<string, string> = {
  // by IP source
  "mobile-proxies": "IP Source",
  "residential-proxies": "IP Source",
  "datacenter-proxies": "IP Source",
  "isp-proxies": "IP Source",
  // by protocol
  "http-proxies": "Protocol",
  "socks-proxy": "Protocol",
  // by position in the traffic path
  "forward-proxy": "Position",
  "reverse-proxy": "Position",
  "log-proxies": "Position",
  "load-balancing": "Position",
  // by access model
  "shared-proxies": "Access",
  "dedicated-proxies": "Access",
  // by underlying IP technology
  "public-and-private-ip-address": "IP Tech",
  "ipv4-vs-ipv6-proxy": "IP Tech",
  "vpn-vs-proxy": "IP Tech",
  // by anonymity level
  "transparent-proxy": "Anonymity",
  "elite-proxies": "Anonymity",
  // combinations
  "backconnect-proxy": "Combo",
};

const FALLBACK_CATEGORY = "Proxy Server";

/** Two cards point at hand-built product routes under `/proxy-server`
 *  (`proxy-server/mobile-proxies/page.tsx` and `.../residential-proxies/page.tsx`),
 *  not at `proxy_article` documents — so they have no Prismic source to read from
 *  and stay declared here. They lead the grid because they're the commercial pages.
 *
 *  Note: five further slugs used to be listed here — facebook, instagram, reddit,
 *  tiktok and youtube proxies. They have no route file, no `proxy_article` document
 *  and no redirect, so every one of them was a 404. They are omitted until the
 *  articles exist in Prismic, at which point they appear automatically. */
const PRODUCT_PAGE_CARDS: DynamicCardItem[] = [
  {
    id: "product-mobile-proxies",
    category: "IP Source",
    title: "Mobile Proxy",
    excerpt:
      "IPs sourced from mobile networks via 4G/5G modems. CGNAT means many real users share each IP, which makes mobile proxies inherently hard for sites to ban.",
    href: "/proxy-server/mobile-proxies",
    date: "May 12, 2026",
    readTime: "8 min read",
    imageUrl: "/proxy-server/mobile-proxies-300x157.jpg",
  },
  {
    id: "product-residential-proxies",
    category: "IP Source",
    title: "Residential Proxy",
    excerpt:
      "Home IPs assigned by ISPs and sourced via SDK-based integrations. They closely resemble real user traffic, which makes them the default choice for serious scraping.",
    href: "/proxy-server/residential-proxies",
    date: "May 10, 2026",
    readTime: "7 min read",
    imageUrl: "/proxy-server/residential-proxies-landing-page-300x157.jpg",
  },
];

/** Every `proxy_article` in Prismic, newest first, preceded by the two product-page
 *  cards. `content_blocks` is fetched because the read time is derived from the real
 *  word count rather than stored — the payload is server-side and cached under the
 *  `prismic` tag in production.
 *
 *  On a Prismic failure the archive falls back to the two product cards rather than
 *  an empty grid, matching how the use-cases archive degrades. */
export async function getProxyArticleCards(): Promise<DynamicCardItem[]> {
  try {
    const client = createClient();
    const docs = await client.getAllByType("proxy_article", {
      fetch: [
        "proxy_article.title",
        "proxy_article.description",
        "proxy_article.featured_image",
        "proxy_article.publish_date",
        "proxy_article.content_blocks",
      ],
      orderings: [{ field: "my.proxy_article.publish_date", direction: "desc" }],
    });

    const articles = docs
      .filter((doc) => !!doc.uid)
      .map((doc) => {
        const uid = doc.uid as string;
        // Raw ISO value, kept only for sorting — comparing the *formatted* date would
        // compare month names ("Apr" before "Dec" before "Jan"), not chronology.
        const publishedAt = (doc.data.publish_date as string) ?? doc.first_publication_date;
        return {
          publishedAt,
          card: {
            id: doc.id,
            category: CATEGORY_BY_UID[uid] ?? FALLBACK_CATEGORY,
            title: (doc.data.title as string) || uid,
            excerpt: (doc.data.description as string) ?? "",
            href: `/proxy-server/${uid}`,
            date: formatDate(publishedAt),
            readTime: getReadingTimeFromBlocks(
              doc.data.content_blocks as unknown as Parameters<typeof getReadingTimeFromBlocks>[0]
            ),
            imageUrl: prismic.isFilled.image(doc.data.featured_image as prismic.ImageField)
              ? ((doc.data.featured_image as prismic.ImageField).url ?? null)
              : null,
          } satisfies DynamicCardItem,
        };
      })
      // `publish_date` is identical across the current documents, so the API ordering
      // alone is not stable. Title breaks the tie and keeps the grid deterministic.
      .sort(
        (a, b) =>
          (b.publishedAt ?? "").localeCompare(a.publishedAt ?? "") ||
          a.card.title.localeCompare(b.card.title)
      )
      .map((entry) => entry.card);

    return [...PRODUCT_PAGE_CARDS, ...articles];
  } catch {
    return PRODUCT_PAGE_CARDS;
  }
}
